import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    getCurrentUser,
    updateCurrentUser,
    uploadUserAvatar,
} from "./account.api.ts";
import type { Profile, ProfileRequest } from "@shyne/shared/api";

interface AccountState {
    data: Profile | null;
    loading: boolean;
    saving: boolean;
    uploadingAvatar: boolean;
    error: string | null;
}

const initialState: AccountState = {
    data: null,
    loading: false,
    saving: false,
    uploadingAvatar: false,
    error: null,
};

export const fetchCurrentUser = createAsyncThunk(
    "account/fetch",
    async () => await getCurrentUser(),
);

export const saveCurrentUser = createAsyncThunk(
    "account/update",
    async (changes: ProfileRequest) => await updateCurrentUser(changes),
);

export const uploadAvatar = createAsyncThunk(
    "account/uploadAvatar",
    async (file: File) => await uploadUserAvatar(file),
);

const slice = createSlice({
    name: "account",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrentUser.pending, (s) => {
                s.loading = true;
                s.data = null;
            })
            .addCase(fetchCurrentUser.fulfilled, (s, a) => {
                s.loading = false;
                s.data = a.payload;
            })
            .addCase(fetchCurrentUser.rejected, (s, a) => {
                s.loading = false;
                s.error = a.error.message ?? "Failed to fetch user.";
            })
            .addCase(saveCurrentUser.pending, (s) => {
                s.saving = true;
            })
            .addCase(saveCurrentUser.fulfilled, (s, a) => {
                s.saving = false;
                s.data = a.payload;
            })
            .addCase(saveCurrentUser.rejected, (s, a) => {
                s.saving = false;
                s.error = a.error.message ?? "Failed to update user.";
            })
            .addCase(uploadAvatar.pending, (s) => {
                s.uploadingAvatar = true;
            })
            .addCase(uploadAvatar.fulfilled, (s, a) => {
                s.uploadingAvatar = false;
                s.data = a.payload;
            })
            .addCase(uploadAvatar.rejected, (s, a) => {
                s.uploadingAvatar = false;
                s.error = a.error.message ?? null;
            });
    },
});

export default slice.reducer;